import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  Stack,
  Paper,
  alpha,
  IconButton,
} from "@mui/material";
import { Add, Delete, Edit, Check, Close, Payments } from "@mui/icons-material";
import { defaultScrollBarStyles } from "../../../../utils/styles/scrollbar";
import { formatAmount } from "../../../../utils/hooks/dataHandler";
import { YearPicker } from "../../../utils/Form/YearPicker";
import { useTheme } from "@mui/material/styles";
import IconButtonWithTooltip from "../../../utils/IconButtonWithTooltip";
import { useErrorDialog } from "../../../../contexts/ErrorDialogContext";

const PrincipalPaidDialog = ({ open, onClose, selectedItem }) => {
  const theme = useTheme();
  const [principalPaid, setPrincipalPaid] = useState(
    selectedItem?.principalPaid || []
  );
  const [newYear, setNewYear] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [editIndex, setEditIndex] = useState(null);
  const [editAmount, setEditAmount] = useState("");

  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  const handleError = useErrorDialog();

  // Reset state when selectedItem changes
  useEffect(() => {
    setPrincipalPaid(selectedItem?.principalPaid || []);
    setNewYear("");
    setNewAmount("");
    setEditIndex(null);
    setEditAmount("");
    setRangeStart("");
    setRangeEnd("");
  }, [open, selectedItem?.assetId]);

  const handleAutoDivide = () => {
    if (!rangeStart || !rangeEnd) {
      handleError("Please select both start and end years.");
      return;
    }

    const start = parseInt(rangeStart);
    const end = parseInt(rangeEnd);

    if (isNaN(start) || isNaN(end) || start > end) {
      handleError("Invalid year range.");
      return;
    }

    const yearsCount = end - start + 1;
    if (yearsCount <= 0) {
      handleError("Year range must include at least 1 year.");
      return;
    }

    const totalCost = parseFloat(selectedItem?.cost || 0);
    if (isNaN(totalCost) || totalCost <= 0) {
      handleError("Invalid asset cost.");
      return;
    }
    const rawDivided = Math.floor(totalCost / yearsCount);

    // Base amount rounded to 2 decimals
    const baseAmount = parseFloat(rawDivided.toFixed(2));

    // Calculate total with baseAmount
    const totalWithBase = baseAmount * yearsCount;

    // Difference caused by rounding
    const diff = parseFloat((totalCost - totalWithBase).toFixed(2));

    const newPayments = [];
    for (let y = start; y <= end; y++) {
      let amount = baseAmount;
      if (y === end) {
        // Adjust last year with diff
        amount = parseFloat((baseAmount + diff).toFixed(2));
      }
      newPayments.push({ year: y, amount });
    }

    setPrincipalPaid([...newPayments].sort((a, b) => a.year - b.year));
  };

  const handleAmountChange = (e, setValue) => {
    let val = e.target.value;

    val = val.split(".")[0] || "0";

    setValue(val);
  };

  const handleAdd = () => {
    if (newYear && newAmount) {
      const parsedAmount = parseFloat(newAmount);
      if (!isNaN(parsedAmount) && parsedAmount >= 0) {
        const updatedPrincipalPaid = [
          ...principalPaid,
          { year: newYear, amount: parsedAmount },
        ].sort((a, b) => a.year - b.year);

        setPrincipalPaid(updatedPrincipalPaid);

        // Auto-select the next available year (first unused year after the one just added)
        const parsedYear = parseInt(newYear, 10);
        if (!isNaN(parsedYear)) {
          const usedYears = new Set(
            updatedPrincipalPaid
              .map((p) => parseInt(p.year, 10))
              .filter((y) => !isNaN(y))
          );
          let nextYear = parsedYear + 1;
          while (usedYears.has(nextYear)) nextYear += 1;
          setNewYear(nextYear);
        } else {
          setNewYear("");
        }
        setNewAmount("");
      } else {
        handleError("Please enter a valid amount.");
      }
    } else {
      handleError("Please fill in both year and amount.");
    }
  };

  const handleDelete = (index) => {
    setPrincipalPaid(principalPaid.filter((_, i) => i !== index));
  };

  const handleEditStart = (index, currentAmount) => {
    setEditIndex(index);
    setEditAmount(currentAmount.toString());
  };

  const handleEditSave = (index) => {
    const parsedAmount = parseFloat(editAmount);
    if (!isNaN(parsedAmount) && parsedAmount >= 0) {
      const updatedPrincipalPaid = principalPaid.map((item, i) =>
        i === index ? { ...item, amount: parsedAmount } : item
      );
      setPrincipalPaid(updatedPrincipalPaid);
      setEditIndex(null);
      setEditAmount("");
    } else {
      handleError("Please enter a valid amount.");
    }
  };

  const handleClearAll = () => {
    setPrincipalPaid([]);
    setNewYear("");
    setNewAmount("");
    setEditIndex(null);
    setEditAmount("");
    setRangeStart("");
    setRangeEnd("");
  };

  const handleSave = () => {
    let toBringPrincipalPaid = principalPaid;
    // If there's a pending edit, save it first
    if (editIndex !== null) {
      const parsedAmount = parseFloat(editAmount);
      if (!isNaN(parsedAmount) && parsedAmount >= 0) {
        const updatedPrincipalPaid = toBringPrincipalPaid.map((item, i) =>
          i === editIndex ? { ...item, amount: parsedAmount } : item
        );
        setPrincipalPaid(updatedPrincipalPaid);

        toBringPrincipalPaid = updatedPrincipalPaid;
      } else {
        handleError("Please enter a valid amount for the pending edit.");
        return; // Don't close dialog if there's an invalid edit
      }
    }

    // Clear edit state
    setEditIndex(null);
    setEditAmount("");

    // Close dialog with updated data
    onClose(toBringPrincipalPaid);
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose(null);
      }}
      disableEscapeKeyDown
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: 680,
          borderRadius: 3,
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 0 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <Payments color='primary' />
          </Box>
          <Box flex={1}>
            <Typography variant='h6' fontWeight={600} color='text.primary'>
              Principal Paid Breakdown
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {selectedItem?.assetName}
            </Typography>
          </Box>
          <IconButton
            onClick={() => onClose(null)}
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: "primary.main",
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Close />
          </IconButton>
        </Paper>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Add Payment Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: (theme) => alpha(theme.palette.success.main, 0.02),
          }}
        >
          <Typography
            variant='subtitle1'
            fontWeight={600}
            color='text.primary'
            sx={{ mb: 0.5 }}
          >
            Add New Payment
          </Typography>
          <Stack direction='row' spacing={2} alignItems='center' sx={{ mb: 2 }}>
            <YearPicker
              label='YA'
              year={newYear}
              onChange={setNewYear}
              reference={{}}
              style={{ width: 120 }}
              isMaxDate={false}
              filterYears={principalPaid.map((item) => item.year)}
            />

            <TextField
              label='Amount'
              value={newAmount}
              onChange={(e) => handleAmountChange(e, setNewAmount)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAdd();
                }
              }}
              size='small'
              sx={{
                bgcolor: "background.paper",
                flexGrow: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "primary.main",
                    boxShadow: (theme) =>
                      `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                  },
                },
              }}
              type='number'
              inputProps={{
                step: 1,
                inputMode: "numeric",
              }}
              variant='outlined'
              placeholder='Enter amount'
              autoComplete='off'
              InputProps={{
                startAdornment: (
                  <Typography
                    variant='body1'
                    color='text.secondary'
                    sx={{ mr: 1 }}
                  >
                    $
                  </Typography>
                ),
              }}
            />
            <Button
              variant='contained'
              color='success'
              startIcon={<Add />}
              onClick={handleAdd}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontSize: 14,
                px: 3,
                boxShadow: "none",
                "&:hover": {
                  boxShadow: (theme) =>
                    `0 4px 12px ${alpha(theme.palette.success.main, 0.2)}`,
                  transform: "translateY(-1px)",
                },
              }}
            >
              Add
            </Button>
          </Stack>

          {process.env.REACT_APP_RENDER_ENV === "DEV" && (
            <Box
              sx={{
                pt: 2,
                mt: 2,
                borderTop: "1px dashed",
                borderColor: (theme) => alpha(theme.palette.info.main, 0.3),
              }}
            >
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ minWidth: "fit-content" }}
              >
                Divide ${formatAmount(selectedItem?.cost)} across years only in
                DEVELOPMENT MODE:
              </Typography>
              <Stack
                direction='row'
                spacing={2}
                alignItems='center'
                sx={{ mt: 2 }}
              >
                <YearPicker
                  label='Start Year'
                  year={rangeStart}
                  onChange={setRangeStart}
                  reference={{}}
                  style={{ width: 100 }}
                  isMaxDate={false}
                />

                <Typography variant='body2' color='text.secondary'>
                  to
                </Typography>

                <YearPicker
                  label='End Year'
                  year={rangeEnd}
                  onChange={setRangeEnd}
                  reference={{}}
                  style={{ width: 100 }}
                  isMaxDate={false}
                />

                <Button
                  variant='outlined'
                  color='info'
                  size='small'
                  onClick={handleAutoDivide}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontSize: 13,
                    px: 2,
                    py: 0.5,
                    borderStyle: "dashed",
                    "&:hover": {
                      borderStyle: "solid",
                      bgcolor: (theme) => alpha(theme.palette.info.main, 0.04),
                    },
                  }}
                >
                  Auto Divide
                </Button>
              </Stack>
            </Box>
          )}
        </Paper>

        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            minHeight: "400px",
            maxHeight: "400px",
            ...defaultScrollBarStyles,
          }}
        >
          <List sx={{ p: 2 }}>
            {principalPaid.length > 0 ? (
              principalPaid
                .sort((a, b) => a.year - b.year)
                .map((item, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      mb: 1,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: (theme) => alpha(theme.palette.divider, 0.1),
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        borderColor: (theme) => theme.palette.primary.main,
                        boxShadow: (theme) =>
                          `0 4px 12px ${alpha(
                            theme.palette.primary.main,
                            0.1
                          )}`,
                      },
                    }}
                  >
                    <ListItem
                      sx={{
                        py: 2,
                        px: 2,
                      }}
                      secondaryAction={
                        <Stack direction='row' spacing={1}>
                          {editIndex === index ? (
                            <IconButtonWithTooltip
                              icon={Check}
                              tooltipTitle='Save'
                              onClick={() => handleEditSave(index)}
                              color='success'
                              sx={{
                                bgcolor: (theme) =>
                                  alpha(theme.palette.success.main, 0.08),
                                "&:hover": {
                                  bgcolor: (theme) =>
                                    alpha(theme.palette.success.main, 0.12),
                                },
                              }}
                            />
                          ) : (
                            <IconButtonWithTooltip
                              icon={Edit}
                              tooltipTitle='Edit'
                              onClick={() =>
                                handleEditStart(index, item.amount)
                              }
                              color='primary'
                              sx={{
                                "&:hover": {
                                  bgcolor: (theme) =>
                                    alpha(theme.palette.primary.main, 0.12),
                                },
                              }}
                            />
                          )}
                          <IconButtonWithTooltip
                            icon={Delete}
                            tooltipTitle='Delete'
                            onClick={() => handleDelete(index)}
                            color='error'
                            disabled={editIndex === index}
                            sx={{
                              "&:hover": {
                                bgcolor: (theme) =>
                                  alpha(theme.palette.error.main, 0.12),
                              },
                            }}
                          />
                        </Stack>
                      }
                    >
                      <Stack
                        direction='row'
                        spacing={2}
                        alignItems='center'
                        sx={{ width: "100%" }}
                      >
                        <Typography
                          variant='subtitle1'
                          fontWeight={500}
                          color='text.primary'
                          sx={{ minWidth: "100px" }}
                        >
                          {item.year}
                        </Typography>
                        {editIndex === index ? (
                          <TextField
                            value={editAmount}
                            onChange={(e) =>
                              handleAmountChange(e, setEditAmount)
                            }
                            size='small'
                            type='number'
                            variant='outlined'
                            sx={{
                              width: "150px",
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                "&:hover fieldset": {
                                  borderColor: "primary.main",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "primary.main",
                                  boxShadow: (theme) =>
                                    `0 0 0 4px ${alpha(
                                      theme.palette.primary.main,
                                      0.1
                                    )}`,
                                },
                              },
                            }}
                            InputProps={{
                              startAdornment: (
                                <Typography
                                  variant='body1'
                                  color='text.secondary'
                                  sx={{ mr: 1 }}
                                >
                                  $
                                </Typography>
                              ),
                            }}
                          />
                        ) : (
                          <Typography
                            variant='subtitle1'
                            color='text.primary'
                            sx={{ flex: 1 }}
                          >
                            {formatAmount(item.amount)}
                          </Typography>
                        )}
                      </Stack>
                    </ListItem>
                  </Paper>
                ))
            ) : (
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: "center",
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
                  border: "1px dashed",
                  borderColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.2),
                  borderRadius: 2,
                }}
              >
                <Payments
                  sx={{
                    fontSize: 48,
                    color: (theme) => alpha(theme.palette.primary.main, 0.3),
                    mb: 2,
                  }}
                />
                <Typography variant='body1' color='text.secondary' gutterBottom>
                  No principal payments recorded
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Add a payment using the form above
                </Typography>
              </Paper>
            )}
          </List>
        </Box>
      </DialogContent>

      <Divider />

      {/* Footer Actions - Clear separation from Add Payment */}
      <DialogActions sx={{ p: 3, gap: 2, justifyContent: "space-between" }}>
        <Button
          onClick={handleClearAll}
          variant='text'
          sx={{
            textTransform: "none",
            color: "error.main",
            "&:hover": {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
            },
          }}
        >
          Clear All Details
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={() => onClose(null)}
            variant='outlined'
            color='primary'
            sx={{
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} variant='contained' color='primary'>
            Save Changes
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PrincipalPaidDialog;
